import { Road } from './road';
import { BuildingType } from './building';
import { WorldPoint } from './grid';
import { Purse } from './resources';
import { JsonDictionaryWrapper } from './utils';
import { BuildingFinder } from './enemy';

export enum CommandViability {
    Ok,

    // no money
    Unaffordable,

    // hitting another building
    Conflict,

    // The road is too long
    TooLong,

    // no resources to build this
    NoResource,
}

export type CreateRoadCommand = {
    kind: 'CreateRoad'
    road: Road
}

export type CreateBuildingCommand = {
    kind: 'CreateBuilding'
    building_type: BuildingType
    position: WorldPoint
}

export type DeleteBuildingCommand = {
    kind: 'DeleteBuilding'
    building: WorldPoint
}

export type DeleteRoadCommand = {
    kind: 'DeleteRoad'
    start: WorldPoint
    finish: WorldPoint
}

export type SendWaveCommand = {
    kind: 'SendWave'
}

export type CommandInner = SendWaveCommand | CreateRoadCommand | CreateBuildingCommand | DeleteBuildingCommand | DeleteRoadCommand

export type Command = {
    cmd: CommandInner
    cost: Purse
    viability: CommandViability
}

// A set of commands
export class Packet {
    public id: number = -1
    public commands: Command[] = []

    public static create_json_command(js: JsonDictionaryWrapper, bf: BuildingFinder): CommandInner {
        switch (js.get_string("kind")) {
            case "SendWave":
                return {
                    kind: "SendWave",
                }

            case "DeleteBuilding":
                return {
                    kind: "DeleteBuilding",
                    building: WorldPoint.create_json(js.get_object("building"))
                };

            case "CreateBuilding":
                return {
                    kind: "CreateBuilding",
                    building_type: js.get_number("building_type"),
                    position: WorldPoint.create_json(js.get_object("position"))
                };

            case "DeleteRoad":
                return {
                    kind: "DeleteRoad",
                    start: WorldPoint.create_json(js.get_object("start")),
                    finish: WorldPoint.create_json(js.get_object("finish")),
                };

            case "CreateRoad":
                return {
                    kind: "CreateRoad",
                    road: Road.create_json(js.get_object("road"), bf),
                };
        }

        throw new Error("Could not deserialise command")
    }

    public static create_json(js: JsonDictionaryWrapper, bf: BuildingFinder): Packet {
        let p = new Packet()
        p.id = js.get_number("id")

        for (let obj of js.get_object_array("commands").all) {
            p.commands.push({
                cost: Purse.create_json(obj.get_object("cost")),
                viability: CommandViability.Ok,
                cmd: Packet.create_json_command(obj.get_object("cmd"), bf),
            })
        }
        return p
    }

    private toJSON(): any {
        return {
            commands: this.commands,
            id: this.id,
        }
    }
}

export interface PacketIO {
    ready(): void
    send(p: Packet): void
    receive(): Packet[]

    onjoin: ((id: number) => void)
    onleave: ((id: number) => void)
}

export class LocalPacketIO implements PacketIO {
    private p: Packet | null = null
    public onjoin: ((id: number) => void) = (id: number) => {}
    public onleave: ((id: number) => void) = (id: number) => {}

    public ready(): void {
        this.onjoin(0)
    }

    public send(packet: Packet): void {
        packet.id = 0
        this.p = packet
    }

    public receive(): Packet[] {
        let ps: Packet[] = []

        if (this.p != null) {
            ps.push(this.p)
            this.p = null
        }

        return ps
    }
}

export class WebSocketIO implements PacketIO {
    private socket: WebSocket | null = null
    private finder: BuildingFinder
    private packets: Packet[] = []
    private id: number = -1
    private out_buffer: Packet[] = []
    private open: boolean = false
    private get_state: ()=>string
    private set_state: (s: string)=>void
    private gid: string

    public onjoin: ((id: number) => void) = (id: number) => {}
    public onleave: ((id: number) => void) = (id: number) => {}

    constructor(world_id: string, state_getter: ()=>string, state_setter: (s: string)=>void, bf: BuildingFinder) {
        this.gid = world_id
        this.get_state = state_getter
        this.set_state = state_setter
        this.finder = bf
    }

    public ready(): void {
        this.socket = new WebSocket("/multiplayer/" + this.gid + "/socket") 
        this.socket.onmessage = evt => this.received_line(evt.data)
        this.socket.onopen = evt => this.socket_did_open()
    }

    private socket_did_open() {
        this.open = true
        for (let p of this.out_buffer) {
            this.send_packet_now(p)
        }
    }

    private received_line(s: string) {
        const cpts = s.split(">")
        if (cpts.length != 2) {
            throw new Error("bad line: " + s)
        }

        switch (cpts[0]) {
            case "getstate": {
                console.log("State request")
                this.socket?.send("state: " + this.get_state() + "\n")
            } break;

            case "setstate": {
                console.log("State set")
                const ns = cpts[1]
                this.set_state(cpts[1])
            } break;

            case "packet": {
                const js = JsonDictionaryWrapper.create_from_string(cpts[1])
                this.packets.push(Packet.create_json(js, this.finder))
            } break;

            case "hello": {
                this.id = parseInt(cpts[1], 10)
                console.log("Connected as ", this.id)
            } break;

            case "disconnected": {
                const new_id = parseInt(cpts[1], 10)
                console.log("Player left ", new_id)
                this.onleave(new_id)
            } break;

            case "connected": {
                const new_id = parseInt(cpts[1], 10)
                console.log("Player joined ", new_id)
                this.onjoin(new_id)
            } break;
        }
    }

    private send_packet_now(packet: Packet): void {
        this.socket?.send(JSON.stringify(packet) + "\n")
    }

    public send(packet: Packet): void {
        packet.id = this.id
        if (this.open) {
            this.send_packet_now(packet)
        } else {
            this.out_buffer.push(packet)
        }
    }

    public receive(): Packet[] {
        const ps = this.packets
        this.packets = []
        return ps
    }
}

export enum TickAction {
    Tick,
    Hold,
}

/*
  Buffer commands for deterministic lockstep to work
 */
export class PacketBuffer {
    private frame: number = 1
    private packet: Packet = new Packet()
    private io: PacketIO
    private users: number[] = []
    private incoming_packets: Record<number, Packet>
    private notify_tick: boolean = false

    // total time period for net sync
    public static syncframe_interval: number = 10

    // on this frame we send our packet
    public static syncframe_send: number = 7

    constructor(pio: PacketIO) {
        this.io = pio
        this.io.onjoin = (id: number) => {
            this.frame = 1
            this.users.push(id)
        };
        this.io.onleave = (id: number) => {
            this.frame = 1
            this.users = this.users.filter(v => v != id)
        };
        this.incoming_packets = {}
        this.reset_packet()

        this.io.ready()
    }

    private reset_packet(): void {
        this.packet = new Packet()
    }

    public add_command(cmd: Command) {
        this.packet.commands.push(cmd)
    }

    // tick, optionally grabbing a set of commands
    public tick(): [Command[] | null, TickAction] {
        const sub_frame = this.frame % PacketBuffer.syncframe_interval

        for (let p of this.io.receive()) {
            this.incoming_packets[p.id] = p
        }

        if (sub_frame == 0) {
            let cmds: Command[] = []

            // check if we have all packets and are able to advance beyond the netframe
            for (let id of this.users) {
                let p = this.incoming_packets[id]
                if (p == undefined) {
                    console.log("Hold advance due to missing packet for ", id)
                    this.notify_tick = true
                    return [null, TickAction.Hold]
                }

                for (let c of p.commands) {
                    cmds.push(c)
                }
            }

            if (this.notify_tick) {
                console.log("Continue tick")
                this.notify_tick = true
            }
            
            // ok to advance beyond netframe
            this.frame += 1
            this.incoming_packets = {}
            return [cmds, TickAction.Tick]
        } else {
            if (sub_frame == PacketBuffer.syncframe_send) {
                this.io.send(this.packet)
                this.reset_packet()
            }

            this.frame += 1
            return [null, TickAction.Tick]
        }
    }
}
